// client/src/api/core/boundApiFetch.ts
//
// UNICO punto dell'intera codebase che importa useAuthStore staticamente.
// Tutti i domain-file (garageApi, routesApi, …) importano da qui —
// mai da apiFetch direttamente — così il grafo delle dipendenze resta aciclico.
//
// Impersonation-aware: usa getEffectiveToken() invece di accessToken diretto,
// in modo che la modalità admin-impersonate sia trasparente a tutti i servizi.
//
// Dipendenze dirette:
//   apiFetch.ts        ← core fetch + error handling
//   useAuthStore       ← l'unico import Zustand dell'intero layer API

import useAuthStore from "@/store/useAuthStore";
import { apiFetch } from "./apiFetch";
import type { ApiFetchExtras, GetAuthStore, AuthStoreSnapshot } from "./apiFetch";

// ── Store adapter ─────────────────────────────────────────────────────────────
//
// Converte la shape reale di AuthState nella shape ridotta AuthStoreSnapshot
// attesa da apiFetch.
// getEffectiveToken() gestisce in modo trasparente la modalità impersonazione:
//   - normale    → accessToken dell'utente loggato
//   - impersonate → accessToken dell'utente impersonato (già sostituito da
//                   startImpersonation, ma usiamo il getter esplicito per
//                   mantenere l'intenzione semantica leggibile)

export const getAuthStoreSnapshot: GetAuthStore = (): AuthStoreSnapshot => {
  const s = useAuthStore.getState();
  return {
    // Impersonation-aware: usa sempre getEffectiveToken()
    accessToken: s.getEffectiveToken(),
    isHydrated: s.isHydrated,
    // updateAccess: la firma reale ha user opzionale → compatibile con unknown
    updateAccess: (accessToken: string, user: unknown) =>
      s.updateAccess(accessToken, user as Parameters<typeof s.updateAccess>[1]),
    logout: s.logout,
    // FIX NO-LOGOUT-RECORDING: expose expireSession (recording-aware logout)
    expireSession: s.expireSession,
  };
};

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Wrapper pre-configurato di `apiFetch` con store Zustand già iniettato.
 *
 * @param path   Percorso API relativo — es. `/api/garage/bikes`
 * @param init   RequestInit standard (method, body, headers, …)
 * @returns      Promise<T> con la risposta tipizzata
 *
 * @example
 * import { boundApiFetch } from '@/api/core/boundApiFetch';
 *
 * export async function garageOverview() {
 *   return boundApiFetch<GarageOverview>('/api/garage/overview');
 * }
 */
export function boundApiFetch<T = unknown>(
  path: string,
  init?: RequestInit & ApiFetchExtras,
): Promise<T> {
  return apiFetch<T>(path, init ?? {}, getAuthStoreSnapshot);
}
